import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { EMPTY, Observable, throwError, TimeoutError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { isNullOrEmpty } from '../../../util/src/lib/is-null-or-empty';
import { ErrorService } from './error.service';
import { AuthenticationService } from '../services/authentication.service';

/**
 * @description 提供設定預設 api timeout 設定之 Injection Token，單位為毫秒，預設為 300000 毫秒。
 */
export const DEFAULT_TIMEOUT = new InjectionToken<number>('defaultTimeout');

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {

  // 判斷是否為第一個 401 error
  private first401Error: boolean = true;

  constructor(
    @Optional() @Inject(DEFAULT_TIMEOUT) protected defaultTimeout: number,
    private logger: NGXLogger,
    private errorService: ErrorService,
    private authenticationService: AuthenticationService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const timeoutValue =
      req.headers.get('timeout') || this.defaultTimeout || 300000;
    const timeoutValueNumeric = Number(timeoutValue);

    return next.handle(req).pipe(
      timeout(timeoutValueNumeric),
      catchError((response) => {
        if (response instanceof TimeoutError) {
          this.logger.error('HttpErrorInterceptor Timeout Error:', response);
          this.errorService.showTimeoutError();
        } else {
          this.logger.error('HttpErrorInterceptor Error:', response);
          switch (response.status) {
            case 401:
              if (this.first401Error) {
                this.first401Error = false
                this.authenticationService.refreshTokenForNoPermission$().subscribe(res => {
                  if (res) {
                    this.first401Error = true;
                  }
                });
              }
              break;
            case 400:
              const errorCode = response.headers.get('X-Error-Code');
              if (isNullOrEmpty(errorCode) === false) {
                const error = this.errorService.handleBusinessError(
                  errorCode,
                  response.error
                );

                if (error) {
                  return throwError(error);
                }
              }
              break;
            case 0:
              this.errorService.showDisconnectedError();
              break;
            default:
              const clientTraceId = req.headers.get('X-Client-TraceId') ?? '';
              this.errorService.handleUnknowError(clientTraceId);

              const isReturnError = req.headers.get('X-ReturnError') ?? '';
              if (isReturnError) {
                throw Error(response);
              }
              break;
          }
        }

        const returnErrorStatus = req.headers.get('return-error');

        // Request header 有 'return-error' 時，返回與其值相等的 status 錯誤，否則不返回錯誤
        return returnErrorStatus === response.status?.toString()
          ? next.handle(req)
          : EMPTY;
      })
    );
  }
}
